import type { CharacterData } from "../../../../utils/game/character/CharacterData";

import { ReactElement, useCallback, useMemo } from "react";
import { WieldMode } from "../../../../utils/game/mechanics/WieldMode";

import AttributePanel from "../AttributePanel/AttributePanel";
import AttributeIconPanel from "../AttributeIconPanel/AttributeIconPanel";
import BorderedPanel from "../../../../common/components/display/BorderedPanel/BorderedPanel";
import WeaponValuesPanel from "./WeaponValuesPanel";

import Flexbox from "../../../../common/components/layout/Flexbox/Flexbox";
import FlexColumn from "../../../../common/components/layout/Flexbox/FlexColumn";
import FlexRow from "../../../../common/components/layout/Flexbox/FlexRow";
import InventoryItemIconSlot from "../InventoryItemSlot/InventoryItemIconSlot";
import StatBoard from "../../../../utils/game/mechanics/StatBoard";

import switchCircleIcon from "../../../../assets/images/common/svg/switch_circle.svg";

import defIcon from "../../../../assets/images/character_sheet/svg/attributes/def.svg";
import mdefIcon from "../../../../assets/images/character_sheet/svg/attributes/mdef.svg";
import dodgeIcon from "../../../../assets/images/character_sheet/svg/attributes/dodge.svg";
import speedIcon from "../../../../assets/images/character_sheet/svg/attributes/speed.svg";

type Props = Readonly<{
    statBoard: StatBoard,
    characterData: CharacterData,
    isEditable?: boolean,
    useIconAttributes?: boolean,
    dmgDisplayMode?: "physical" | "magic",
    validWieldModes?: ReadonlyArray<WieldMode>,
    wieldMode?: WieldMode,
    onAttributeTitleClick?: (attributeName: string) => void,
    onDmgDisplayModeChange?: (mode: "physical" | "magic") => void,
    onWieldModeChange?: (mode: WieldMode) => void,
}>;

const defaultValidWieldModes = [WieldMode.RIGHT_HAND_WIELD];

export default function CombatStatsContainer({
    statBoard,
    characterData,
    isEditable = false,
    useIconAttributes = true, 
    dmgDisplayMode = "physical",
    validWieldModes = defaultValidWieldModes,
    wieldMode = WieldMode.RIGHT_HAND_WIELD,
    onAttributeTitleClick,
    onDmgDisplayModeChange,
    onWieldModeChange,
}: Props): ReactElement {
    const isLHActive = useMemo(() => {
        return wieldMode === WieldMode.DUAL_WIELD 
            || wieldMode === WieldMode.LEFT_HAND_WIELD 
            || (wieldMode === WieldMode.TWO_HAND_WIELD && characterData.lh != null);
    }, [characterData.lh, wieldMode]);

    const isRHActive = useMemo(() => {
        return wieldMode === WieldMode.DUAL_WIELD 
            || wieldMode === WieldMode.RIGHT_HAND_WIELD 
            || (wieldMode === WieldMode.TWO_HAND_WIELD && characterData.rh != null);
    }, [characterData.rh, wieldMode]);

    const cycleWieldMode = useCallback((prev: WieldMode): WieldMode => {
        if(validWieldModes.length === 0) {
            return WieldMode.RIGHT_HAND_WIELD;
        }

        const prevIndex = validWieldModes.indexOf(prev);
        

        const newIndex = prevIndex < 0 || prevIndex >= validWieldModes.length - 1 ? 0 : prevIndex + 1;

        return validWieldModes[newIndex];
    }, [validWieldModes]);

    return <FlexColumn>
        <FlexRow alignment="center">
            <div
                style={{
                    fontSize: "80%",
                    fontWeight: "bold",
                    marginTop: "1px",
                    padding: "4px 40px",
                    border: "1px solid black",
                    borderRadius: "12px",
                    userSelect: "none",
                    backgroundColor: "#b1bbbd"
                }}
            >
                Combat
            </div>
        </FlexRow>
        <FlexRow>
            <FlexColumn
                gap={8}
                style={{
                    margin: 4,
                    padding: "12px",
                    height: "100%",
                    border: "1px dotted black",
                    borderRadius: 8
                }} 
                width="half"
            >
                {useIconAttributes ? 
                    <AttributeIconPanel 
                        icon={defIcon} 
                        statBoard={statBoard} 
                        statKey="DEF"
                        onIconClick={() => {
                            onAttributeTitleClick?.("Physical Defense");
                        }}
                    /> :
                    <AttributePanel
                        statBoard={statBoard}
                        statKey="DEF"
                        onAttributeTitleClick={() => {
                            onAttributeTitleClick?.("Physical Defense");
                        }}
                    />
                }
                {useIconAttributes ? 
                    <AttributeIconPanel 
                        icon={mdefIcon} 
                        statBoard={statBoard} 
                        statKey="MDEF"
                        onIconClick={() => {
                            onAttributeTitleClick?.("Magic Defense");
                        }}
                    /> :
                    <AttributePanel
                        statBoard={statBoard}
                        statKey="DEF"
                        onAttributeTitleClick={() => {
                            onAttributeTitleClick?.("Magic Defense");
                        }}
                    />
                }
                {useIconAttributes ? 
                    <AttributeIconPanel 
                        icon={dodgeIcon} 
                        statBoard={statBoard} 
                        statKey="DDG"
                        onIconClick={() => {
                            onAttributeTitleClick?.("Dodge/Block");
                        }}
                    /> :
                    <AttributePanel
                        statBoard={statBoard}
                        statKey="DDG"
                        onAttributeTitleClick={() => {
                            onAttributeTitleClick?.("Dodge/Block");
                        }}
                    />
                }
                {useIconAttributes ? 
                    <AttributeIconPanel 
                        icon={speedIcon} 
                        statBoard={statBoard} 
                        statKey="SPD"
                        onIconClick={() => {
                            onAttributeTitleClick?.("Speed");
                        }}
                    /> :
                    <AttributePanel
                        statBoard={statBoard}
                        statKey="DEF"
                        onAttributeTitleClick={() => {
                            onAttributeTitleClick?.("Speed");
                        }}
                    />
                }
            </FlexColumn>
            <FlexColumn
                style={{
                    padding: "8px 4px 4px 4px",
                    height: "100%",
                }}
            >
                <FlexColumn>
                    <FlexRow alignment="center" width="full" gap={8}>
                        <FlexRow
                            gap={4}
                            style={{
                                marginBottom: 8,
                                padding: 4,
                                borderRadius: 4,
                                borderColor: "black",
                                borderStyle: "solid",
                                borderWidth: 1,
                            }}
                            width="auto"
                        >
                            {characterData.lh == null &&
                                characterData.rh == null && (
                                    <InventoryItemIconSlot
                                        id="lh"
                                        isEditable={false}
                                        item={null}
                                        emptyIconSrc="gloves_blank"
                                        tooltipEnabled={false}
                                    />
                                )}
                            {characterData.lh != null && (
                                <InventoryItemIconSlot
                                    id="lh"
                                    isEditable={false}
                                    item={characterData.lh}
                                    semiTransparent={!isLHActive}
                                />
                            )}
                            {characterData.rh != null && (
                                <InventoryItemIconSlot
                                    id="rh"
                                    isEditable={false}
                                    item={characterData.rh}
                                    semiTransparent={!isRHActive}
                                />
                            )}
                        </FlexRow>
                    </FlexRow>
                    {isEditable && characterData.lh != null && characterData.rh != null && (
                        <FlexRow alignment="center" width="full" gap={8}>
                            <span style={{textAlign: "end", fontSize: "0.8rem", paddingTop: 4}}>
                                {wieldMode === WieldMode.DUAL_WIELD ? "Dual Wield"
                                    : wieldMode === WieldMode.LEFT_HAND_WIELD ? "Left Hand"
                                    : wieldMode === WieldMode.RIGHT_HAND_WIELD ? "Right Hand"
                                    : wieldMode === WieldMode.TWO_HAND_WIELD ? "Two Handed"
                                    : ""
                                }
                            </span>
                            <button
                                className="dmg-switch-button"
                                onClick={(e) => {
                                    const newWieldMode = cycleWieldMode(wieldMode);

                                    if(newWieldMode !== wieldMode) {
                                        onWieldModeChange?.(newWieldMode);
                                    }
                                }}
                            >
                                <img src={switchCircleIcon} alt="Switch" />
                            </button>
                        </FlexRow>
                    )}
                </FlexColumn>
                <WeaponValuesPanel 
                    characterData={characterData}
                    statBoard={statBoard}
                    dmgDisplayMode={dmgDisplayMode}
                    useIconAttributes={useIconAttributes}
                    wieldMode={wieldMode}
                    onAttributeTitleClick={onAttributeTitleClick}
                />
                <FlexRow
                    alignment="end"
                    style={{ padding: 4 }}
                    width="full"
                    gap={8}
                >
                    <Flexbox direction="column" justifyContent="center">
                        <span style={{textAlign: "end", fontSize: '0.8rem'}}>{dmgDisplayMode === 'magic' ? "Magic Attack" : "Physical Attack"}</span>
                    </Flexbox>
                    <button
                        className="dmg-switch-button"
                        onClick={(e) => {
                            onDmgDisplayModeChange?.(
                                dmgDisplayMode === "physical"
                                    ? "magic"
                                    : "physical"
                            );
                        }}
                    >
                        <img src={switchCircleIcon} alt="Switch" />
                    </button>
                </FlexRow>
                
            </FlexColumn>
        </FlexRow>
    </FlexColumn>
}